import type { CountStatus, LiveVideoStatistics } from '@discovr/types';
import { fmtDate, fmtDayMonth } from './dateUtils';
import { formatIntegerIN } from './format';
import { lookupOr } from './lookupOr';

export type CountKind = 'views' | 'likes' | 'comments';

/** The last count read before a count stopped being current. */
export interface PreviousCount {
  value: number;
  /** Day and month it stopped being current ("3 Oct"); null when unknown. */
  since: string | null;
}

/** What a count cell shows: a current number, or a label in its place with the previous count under it. */
export interface CountDisplay {
  value: number | null;
  label: string | null;
  previous: PreviousCount | null;
  tooltip: string | null;
}

/** How much of the previous count a cell has room for: "4,474 before 3 Oct" or "was 4.5K". */
export type CaptionLength = 'full' | 'short';

/** Nothing to show yet: the cell renders a dash. */
export const EMPTY_COUNT: CountDisplay = { value: null, label: null, previous: null, tooltip: null };

// Keyed by string so a state the backend adds later falls back instead of printing "undefined".
// "unknown" has no label: an unreadable count shows a dash.
const LABEL: Readonly<Record<string, string | null>> = {
  hidden: 'Hidden',
  off: 'Off',
  not_applicable: 'N/A',
};

const NOUN: Record<CountKind, string> = { views: 'view', likes: 'like', comments: 'comment' };
const PLURAL: Record<CountKind, string> = { views: 'Views', likes: 'Likes', comments: 'Comments' };

const unknownTooltip = (kind: CountKind) => `We couldn't read the ${NOUN[kind]} count on the last update.`;

const TOOLTIP: Readonly<Record<string, (kind: CountKind) => string>> = {
  hidden: (kind) => `The creator has hidden the ${NOUN[kind]} count on this post.`,
  off: (kind) => `${PLURAL[kind]} are turned off on this post.`,
  not_applicable: (kind) => `This post type has no ${NOUN[kind]} count.`,
  unknown: unknownTooltip,
};

const KEPT_COUNT_USE = "It's in totals but not in the engagement rate.";

const LEGACY_HIDDEN_LIKES = -1;
// Go marshals an unset time.Time as 0001-01-01T00:00:00Z.
const GO_ZERO_TIME_PREFIX = '0001-';

/** A since date worth printing: present, not Go's zero time, and parseable. */
function hasSince(since: string): boolean {
  return Boolean(since) && !since.startsWith(GO_ZERO_TIME_PREFIX) && !Number.isNaN(Date.parse(since));
}

interface RawCount {
  count: number;
  status?: CountStatus;
}

function rawCount(stats: LiveVideoStatistics, kind: CountKind): RawCount {
  switch (kind) {
    case 'views':
      return { count: stats.view_count, status: stats.views_status };
    case 'likes':
      return { count: stats.like_count, status: stats.likes_status };
    case 'comments':
      return { count: stats.comment_count, status: stats.comments_status };
  }
}

/** Mirrors the backend's LiveVideoStatistics.Count: legacy negatives read as hidden likes, anything else unusable as unknown. */
function normalised(stats: LiveVideoStatistics, kind: CountKind): RawCount {
  const { count, status } = rawCount(stats, kind);
  if (Number.isFinite(count) && count >= 0) return { count, status };
  const state = kind === 'likes' && count === LEGACY_HIDDEN_LIKES ? 'hidden' : 'unknown';
  return { count: 0, status: { state, since: '' } };
}

/** Mirrors the backend's Fetched: a count read on the latest update, or comments turned off (a real 0). */
function isCurrent(status: CountStatus | undefined): boolean {
  return !status || status.state === 'off';
}

function keptTooltip(reason: string, count: number, since: string): string {
  const before = hasSince(since) ? `, before ${fmtDate(since)}` : '';
  return `${reason} The last count we read was ${formatIntegerIN(count)}${before}. ${KEPT_COUNT_USE}`;
}

/** The one display rule for an engagement count, shared by every screen. */
export function describeCount(stats: LiveVideoStatistics | null | undefined, kind: CountKind): CountDisplay {
  if (!stats) return EMPTY_COUNT;
  const { count, status } = normalised(stats, kind);
  if (!status) return { ...EMPTY_COUNT, value: count };
  const label = lookupOr(LABEL, status.state, null);
  const reason = lookupOr(TOOLTIP, status.state, unknownTooltip)(kind);
  if (!status.kept) return { value: null, label, previous: null, tooltip: reason };
  // A kept count is not a reading of now, so it never takes the number's place.
  return {
    value: null,
    label,
    previous: { value: count, since: hasSince(status.since) ? fmtDayMonth(status.since) : null },
    tooltip: keptTooltip(reason, count, status.since),
  };
}

/** The caption under a label: the previous count, dated when there's room. */
export function previousCaption(
  previous: PreviousCount | null,
  format: (n: number) => string,
  length: CaptionLength,
): string | null {
  if (!previous) return null;
  const value = format(previous.value);
  if (length === 'short' || !previous.since) return `was ${value}`;
  return `${value} before ${previous.since}`;
}

/** The count when it was read on the latest update, else null. */
export function currentCount(stats: LiveVideoStatistics | null | undefined, kind: CountKind): number | null {
  if (!stats) return null;
  const { count, status } = normalised(stats, kind);
  return isCurrent(status) ? count : null;
}

